import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Image,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import { FaTrophy } from "react-icons/fa";

import WinnerRank, { WinnerName } from "../components/WinnerRank";
import PageHeader from "../components/PageHeader";
import { defaultMatchweek } from "../utils/plMatchweek";
import { fixtureStatus, formatKickoff as fmtKickoff } from "../utils/fixtureStatus";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  getCurrentMatchweek,
  getLeaderboard,
  getHalfSeasonLeaderboard,
  getLastClosedMatchweekPicks,
  getMatchweekSummary,
  getLatestStandings,
  getSmackMessages,
  postSmackMessage,
  getSmackReplies,
  postSmackReply,
  reactSmack,
  deleteSmackMessage,
} from "../api/premierLeague";
import { getPoolPredictions } from "../api/tablePredictor";
import { getPool, getPlPayouts } from "../api/pools";
import { usePlSeason } from "../hooks/usePlSeason";
import RulesPayoutsPanel from "../components/RulesPayoutsPanel";
import SmackBoard from "../components/SmackBoard";
import ExpandedTableModal from "../components/ExpandedTableModal";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";
const DIVIDER_GRAY = "#7a7a7a";
const MAX_MATCHWEEK = 38;
const TOP_ROW_BOX_HEIGHT = "560px";
const RANK_COL_WIDTH = "48px";
const USERNAME_COL_WIDTH = "120px";

// The pool leaderboard's own frozen columns, kept separate from the widths
// ExpandedTableModal uses for its frozen pair. On a narrow phone the pair took
// 168px of a ~360px viewport and the scrolling columns carried 16px of padding
// a side, so PTS was barely on screen. Rank keeps room for the champion's
// trophy.
const LB_RANK_COL_WIDTH = { base: "42px", md: RANK_COL_WIDTH };
const LB_USERNAME_COL_WIDTH = { base: "94px", md: USERNAME_COL_WIDTH };
// Room for the text inside the username cell, once its padding is taken off.
const LB_USERNAME_TEXT_WIDTH = { base: "82px", md: "104px" };
const LB_FROZEN_COL_PX = { base: 1.5, md: 4 };
const LB_DATA_COL_PX = { base: 2, md: 4 };

const GOLD = "#c9a227";

function FinalResultsBox({ leaderboardRows, poolPayouts, tablePredictorData, halfLeaderRows, poolName }) {
  const sortedPayouts = [...poolPayouts].sort((a, b) => a.sort_order - b.sort_order);

  const preds = tablePredictorData?.predictions || [];
  const lockedScores = preds.filter((p) => p.score != null).map((p) => p.score);
  const minPredScore = lockedScores.length > 0 ? Math.min(...lockedScores) : null;
  const tableChampNames = preds
    .filter((p) => p.score === minPredScore)
    .map((p) => p.username)
    .join(", ");

  // Ranks are dense, so a genuine tie puts several players at rank 1 — list
  // them all, the same way the table predictor reports joint winners.
  const halfLeaders = halfLeaderRows.filter((r) => r.rank === 1);
  const halfLeader = halfLeaders[0] || null;
  const halfLeaderName =
    halfLeaders.length > 0
      ? halfLeaders.map((r) => r.user || r.username).join(", ")
      : null;

  // Assign a user to each payout by keyword matching on the label
  let leaderboardIdx = 0;
  const payoutRows = sortedPayouts.map((payout) => {
    const lbl = payout.label.toLowerCase();
    let assignedUser = null;
    let isManual = false;
    let leaderboardRow = null;
    let halfRow = null;
    let isChampion = false;

    if (lbl.includes("predictor")) {
      assignedUser = tableChampNames || null;
    } else if (lbl.includes("half")) {
      assignedUser = halfLeaderName;
      isManual = !halfLeaderName;
      halfRow = halfLeader || null;
    } else {
      leaderboardRow = leaderboardRows[leaderboardIdx];
      assignedUser = leaderboardRow ? (leaderboardRow.user || leaderboardRow.username) : null;
      if (leaderboardIdx === 0) isChampion = true;
      leaderboardIdx++;
    }

    return { payout, assignedUser, isManual, leaderboardRow, halfRow, isChampion };
  });

  // Swap half and predictor card positions
  const halfIdx = payoutRows.findIndex(({ payout }) => payout.label.toLowerCase().includes("half"));
  const predIdx = payoutRows.findIndex(({ payout }) => payout.label.toLowerCase().includes("predictor"));
  if (halfIdx !== -1 && predIdx !== -1) {
    [payoutRows[halfIdx], payoutRows[predIdx]] = [payoutRows[predIdx], payoutRows[halfIdx]];
  }

  return (
    <Box
      bg="white"
      color="black"
      border={`3px solid ${GOLD}`}
      borderRadius="lg"
      boxShadow="lg"
      mb={4}
      overflow="hidden"
    >
      <Box bg={GOLD} px={6} py={3}>
        {/* Mobile: pool name on row 1, "FINAL RESULTS" on row 2 */}
        <Flex direction="column" align="center" gap={0} display={{ base: "flex", md: "none" }}>
          <HStack justify="center" spacing={2}>
            <Box color="white"><FaTrophy size={16} /></Box>
            <Heading fontFamily="'Orbitron', sans-serif" fontSize="14px" letterSpacing="2px" fontWeight="700" color="white" textAlign="center">
              {poolName ? poolName.toUpperCase() : "SEASON"}
            </Heading>
            <Box color="white"><FaTrophy size={16} /></Box>
          </HStack>
          <Heading fontFamily="'Orbitron', sans-serif" fontSize="14px" letterSpacing="2px" fontWeight="700" color="white" textAlign="center">
            FINAL RESULTS
          </Heading>
        </Flex>
        {/* Desktop: single row */}
        <HStack justify="center" spacing={3} display={{ base: "none", md: "flex" }}>
          <Box color="white"><FaTrophy size={20} /></Box>
          <Heading fontFamily="'Orbitron', sans-serif" fontSize="22px" letterSpacing="3px" fontWeight="700" color="white">
            {poolName ? `${poolName.toUpperCase()} FINAL RESULTS` : "SEASON FINAL RESULTS"}
          </Heading>
          <Box color="white"><FaTrophy size={20} /></Box>
        </HStack>
      </Box>

      <Box px={6} py={4}>
        <Flex gap={4} align="stretch" flexWrap={{ base: "wrap", md: "nowrap" }}>
          {payoutRows.map(({ payout, assignedUser, isManual, leaderboardRow, halfRow, isChampion }, i) => {
            const lbl = payout.label.toLowerCase();
            const isPredictor = lbl.includes("predictor");
            const isGold = isChampion || isPredictor;
            const borderColor = isGold ? GOLD : TOP_BAR_BLUE;
            const bg = "yellow.50";
            const iconColor = isGold ? "yellow.500" : TOP_BAR_BLUE;
            const stat = isPredictor && minPredScore != null
              ? `${minPredScore} pts`
              : halfRow
              ? `${halfRow.points} pts · GD ${halfRow.goal_differential ?? 0}`
              : leaderboardRow
              ? `${leaderboardRow.points} pts · GD ${leaderboardRow.goal_differential ?? 0}`
              : null;

            return (
              <Box
                key={i}
                bg={bg}
                border="2px solid"
                borderColor={borderColor}
                borderRadius="lg"
                p={isChampion ? 6 : 4}
                flex="1"
                minW={isChampion ? { base: "100%", md: 0 } : { base: "calc(50% - 8px)", md: 0 }}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <VStack spacing={isChampion ? 3 : 2} align="center">
                  <Box color={iconColor} fontSize={isChampion ? "48px" : "28px"} lineHeight="1">
                    <FaTrophy />
                  </Box>
                  <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500" letterSpacing="1px" textAlign="center">
                    {payout.label}
                  </Text>
                  <Text fontSize={isChampion ? "2xl" : "lg"} fontWeight="bold" color={isManual ? "gray.400" : TOP_BAR_BLUE} textAlign="center">
                    {isManual ? "TBD" : (assignedUser || "—")}
                  </Text>
                  {stat && (
                    <Text fontSize="sm" color="gray.600">{stat}</Text>
                  )}
                  <Text fontSize={isChampion ? "xl" : "lg"} fontWeight="bold" color={GOLD}>
                    ${Number(payout.amount).toFixed(2)}
                  </Text>
                </VStack>
              </Box>
            );
          })}
        </Flex>
      </Box>
    </Box>
  );
}

const PL_RULES = [
  {
    heading: "Picking",
    lines: [
      "Pick one team each matchweek.",
      "Assign confidence points (1–5) to each pick.",
      "Team may only be picked once per half of the season.",
      "Limit (4) on how often you can pick against the same opponent per half of the season."
    ],
  },
  {
    heading: "Scoring",
    lines: [
      "Correct pick: +confidence points.",
      "Wrong pick: −confidence points.",
      "Draw: 0 points regardless of pick.",
      "Shutout bonus: +2 pts if your team wins and keeps a clean sheet.",
    ],
  },
  {
    heading: "Table Predictor",
    lines: [
      "Submit your predicted final Premier League table before the season locks.",
    ],
  },
];

function normalizeLeaderboardRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
}

function normalizeFixtures(summaryData) {
  if (Array.isArray(summaryData?.fixtures)) return summaryData.fixtures;
  return [];
}

function normalizeStandingsRows(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  return [];
}


function getTeamName(teamValue) {
  if (!teamValue) return "-";
  if (typeof teamValue === "string") return teamValue;
  return teamValue.name || teamValue.team_name || "-";
}

export default function PremierLeagueActiveHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { poolId } = useParams();
  const location = useLocation();
  const { seasonId: _hookSeasonId } = usePlSeason();
  const SEASON_ID = location.state?.seasonId ?? _hookSeasonId;
  const isDesktop = useBreakpointValue({ base: false, md: true }) ?? false;

  const {
    isOpen: isExpandedTableOpen,
    onOpen: onExpandedTableOpen,
    onClose: onExpandedTableClose,
  } = useDisclosure();

  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [currentMwMeta, setCurrentMwMeta] = useState(null);
  const [selectedMatchweek, setSelectedMatchweek] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [standingsRows, setStandingsRows] = useState([]);
  const [lastClosedPicksData, setLastClosedPicksData] = useState(null);
  const [tablePredictorData, setTablePredictorData] = useState(null);
  const [poolName, setPoolName] = useState("");
  const [poolDetail, setPoolDetail] = useState(null);
  const [poolPayouts, setPoolPayouts] = useState([]);
  const [halfLeaderRows, setHalfLeaderRows] = useState([]);

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingMatchweek, setLoadingMatchweek] = useState(false);
  const [pageError, setPageError] = useState("");

  const username = user?.username || "Player";

  const numericPoolId = Number(poolId);
  const fixtures = useMemo(() => normalizeFixtures(summaryData), [summaryData]);

  const seasonComplete = currentMwMeta?.season_complete === true;
  const isArchived = !!poolDetail?.is_archived;

  const tableWinnerScore = useMemo(() => {
    if (!seasonComplete || !tablePredictorData?.locked) return null;
    const scores = (tablePredictorData?.predictions || [])
      .map((p) => p.score)
      .filter((s) => s != null);
    return scores.length > 0 ? Math.min(...scores) : null;
  }, [seasonComplete, tablePredictorData]);

  const lastPickByUsername = useMemo(() => {
    const map = {};
    for (const row of (lastClosedPicksData?.rows || [])) {
      map[(row.username || "").trim().toLowerCase()] = row;
    }
    return map;
  }, [lastClosedPicksData]);

  useEffect(() => {
    async function loadPage() {
      if (!numericPoolId || Number.isNaN(numericPoolId)) {
        setPageError("Invalid pool.");
        setLoadingPage(false);
        return;
      }

      try {
        setLoadingPage(true);
        setPageError("");

        // Pool must load — if this fails it's a real error
        const [poolData, payouts] = await Promise.all([
          getPool(SEASON_ID, numericPoolId),
          getPlPayouts(SEASON_ID, numericPoolId).catch(() => []),
        ]);
        setPoolDetail(poolData);
        setPoolName(poolData?.name || "");
        setPoolPayouts(payouts);

        // Season data may not exist yet for upcoming seasons — soft-fail
        const [leaderboard, matchweekMeta, latestStandings, lastClosedPicks, poolPredictions, halfLeader] = await Promise.all([
          getLeaderboard(numericPoolId, SEASON_ID).catch(() => []),
          getCurrentMatchweek(SEASON_ID).catch(() => null),
          getLatestStandings(SEASON_ID).then(normalizeStandingsRows).catch(() => []),
          getLastClosedMatchweekPicks(numericPoolId, SEASON_ID).catch(() => null),
          getPoolPredictions(numericPoolId, SEASON_ID).catch(() => null),
          getHalfSeasonLeaderboard(numericPoolId, SEASON_ID).catch(() => ({ rows: [] })),
        ]);

        setLeaderboardRows(normalizeLeaderboardRows(leaderboard));
        setCurrentMwMeta(matchweekMeta);
        setStandingsRows(latestStandings);
        setLastClosedPicksData(lastClosedPicks);
        setTablePredictorData(poolPredictions);
        setHalfLeaderRows(normalizeLeaderboardRows(halfLeader));

        // Advances to the next matchweek once the current one has finished,
        // instead of sitting on a completed round.
        const mw = defaultMatchweek(matchweekMeta);
        setSelectedMatchweek(mw);

        if (matchweekMeta) {
          const summary = await getMatchweekSummary(numericPoolId, mw, SEASON_ID).catch(() => null);
          setSummaryData(summary);
        }
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "Unable to load pool.";
        setPageError(typeof msg === "string" ? msg : "Unable to load pool.");
      } finally {
        setLoadingPage(false);
      }
    }

    if (SEASON_ID) loadPage();
  }, [numericPoolId, SEASON_ID]);

  async function changeMatchweek(nextMw) {
    if (!nextMw || nextMw < 1 || nextMw > MAX_MATCHWEEK) return;
    if (!numericPoolId || Number.isNaN(numericPoolId)) return;

    try {
      setLoadingMatchweek(true);
      setPageError("");
      setSelectedMatchweek(nextMw);

      const summary = await getMatchweekSummary(numericPoolId, nextMw, SEASON_ID);
      setSummaryData(summary);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Unable to load matchweek.";
      setPageError(typeof msg === "string" ? msg : "Unable to load matchweek.");
    } finally {
      setLoadingMatchweek(false);
    }
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  function handleMakeReviewPicks() {
    navigate(`/premier-league/picks/${numericPoolId}`, { state: { seasonId: SEASON_ID } });
  }

  function handleReturnToLobby() {
    navigate("/premier-league");
  }

  function isLoggedInUserRow(row) {
    const rowName = (row?.user || row?.username || "")
      .toString()
      .trim()
      .toLowerCase();
    return rowName && rowName === username.trim().toLowerCase();
  }

  if (loadingPage) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <Flex minH="100vh" align="center" justify="center" direction="column" gap={4}>
          <Spinner size="xl" thickness="4px" />
          <Text>Loading Premier League Pick ’Em...</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white">
      <PageHeader
        title="PL PICK 'EM HOMEPAGE"
        onBack={handleReturnToLobby}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={8}>
        {pageError ? (
          <Alert status="error" borderRadius="md" mb={6}>
            <AlertIcon />
            {pageError}
          </Alert>
        ) : null}

        {!pageError && !currentMwMeta && poolDetail && (
          <Alert status="info" borderRadius="md" mb={6} color={TOP_BAR_BLUE}>
            <AlertIcon />
            Season data is not yet available — fixtures will appear here once the season begins.
          </Alert>
        )}

        {isArchived ? (
          <>
            <FinalResultsBox
              leaderboardRows={leaderboardRows}
              poolPayouts={poolPayouts}
              tablePredictorData={tablePredictorData}
              halfLeaderRows={halfLeaderRows}
              poolName={poolName}
            />
            <Box
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={6}
            >
              <Heading size="md" color={TOP_BAR_BLUE} mb={4}>{poolName ? `${poolName} Final Leaderboard` : "Final Leaderboard"}</Heading>
              <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Rank</Th>
                    <Th>Username</Th>
                    <Th isNumeric>Points</Th>
                    <Th isNumeric>GD</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {leaderboardRows.length > 0 ? (
                    leaderboardRows.map((row, index) => {
                      const isCurrentUser = isLoggedInUserRow(row);
                      return (
                        <Tr key={`${row?.user || row?.username || "row"}-${index}`} bg={isCurrentUser ? "yellow.100" : "transparent"}>
                          <Td fontWeight={isCurrentUser ? "bold" : "normal"}>
                            <WinnerRank rank={row?.rank} finished />
                          </Td>
                          <Td fontWeight={isCurrentUser ? "bold" : "normal"}>{row?.user || row?.username || "-"}</Td>
                          <Td isNumeric fontWeight={isCurrentUser ? "bold" : "normal"}>{row?.points ?? 0}</Td>
                          <Td isNumeric fontWeight={isCurrentUser ? "bold" : "normal"}>{row?.goal_differential ?? 0}</Td>
                        </Tr>
                      );
                    })
                  ) : (
                    <Tr><Td colSpan={4}><Text color="gray.600">No data available.</Text></Td></Tr>
                  )}
                </Tbody>
              </Table>
              </Box>
            </Box>
          </>
        ) : (
          <>
            {seasonComplete && (
              <FinalResultsBox
                leaderboardRows={leaderboardRows}
                poolPayouts={poolPayouts}
                tablePredictorData={tablePredictorData}
                halfLeaderRows={halfLeaderRows}
                poolName={poolName}
              />
            )}

            <SmackBoard
              poolId={numericPoolId}
              seasonId={SEASON_ID}
              apiFns={{ getMessages: getSmackMessages, postMessage: postSmackMessage, getReplies: getSmackReplies, postReply: postSmackReply, react: reactSmack, deleteMessage: deleteSmackMessage }}
            />

        <HStack spacing={3} mb={4} flexWrap="wrap">
          <Button
            bg="red.600"
            color="white"
            onClick={handleMakeReviewPicks}
            _hover={{ bg: "red.700" }}
          >
            Make/Review Picks
          </Button>
          <Box flex="1" display={{ base: "none", md: "block" }} />
          {(poolDetail?.owner_user_id === user?.id || poolDetail?.current_user_is_co_admin) && (
            <Button
              size="sm"
              variant="outline"
              color="white"
              borderColor="whiteAlpha.500"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={() => navigate(`/premier-league/setup/${numericPoolId}`, { state: { seasonId: SEASON_ID } })}
            >
              Pool Settings
            </Button>
          )}
        </HStack>

        <HStack align="stretch" spacing={6} flexWrap="wrap">
          {/* Leaderboard — 2/3 width */}
          <Box
            flex="2"
            minW={{ base: "100%", md: "480px" }}
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={6}
            minH={TOP_ROW_BOX_HEIGHT}
            display="flex"
            flexDirection="column"
          >
            <VStack align="stretch" spacing={4} flex="1">
              <Flex align="center" justify="space-between">
                <Heading size="md" color={TOP_BAR_BLUE}>
                  {poolName ? `${poolName} Leaderboard` : "Leaderboard"}
                </Heading>
                <HStack spacing={3} flexShrink={0}>
                  {lastClosedPicksData?.matchweek ? (
                    <Text fontSize="sm" color="gray.500">
                      Last Pick: MW{lastClosedPicksData.matchweek}
                    </Text>
                  ) : null}
                  <Button
                    size="xs"
                    bg={TOP_BAR_BLUE}
                    color="white"
                    _hover={{ bg: "#000080" }}
                    onClick={() => navigate(`/premier-league/all-picks/${poolId}`,
                                            { state: { seasonId: SEASON_ID } })}
                  >
                    All Picks
                  </Button>
                </HStack>
              </Flex>

              <Box overflowX="auto" flex="1">
                <Table size="sm" variant="simple" sx={{ borderCollapse: "separate", borderSpacing: 0 }}>
                  <Thead>
                    <Tr>
                      {/* Spelled out, these two labels set the columns wider
                          than their contents need on a phone. */}
                      <Th position="sticky" left={0} zIndex={3} bg="white"
                          w={LB_RANK_COL_WIDTH} minW={LB_RANK_COL_WIDTH}
                          px={LB_FROZEN_COL_PX}>{isDesktop ? "Rank" : "#"}</Th>
                      <Th position="sticky" left={LB_RANK_COL_WIDTH} zIndex={3} bg="white"
                          w={LB_USERNAME_COL_WIDTH} minW={LB_USERNAME_COL_WIDTH}
                          px={LB_FROZEN_COL_PX}>{isDesktop ? "Username" : "User"}</Th>
                      <Th isNumeric px={LB_DATA_COL_PX} whiteSpace="nowrap">PTS</Th>
                      <Th isNumeric px={LB_DATA_COL_PX} whiteSpace="nowrap">GD</Th>
                      <Th isNumeric px={LB_DATA_COL_PX} whiteSpace="nowrap">Win Streak</Th>
                      <Th px={LB_DATA_COL_PX} whiteSpace="nowrap">Last Pick</Th>
                      <Th isNumeric px={LB_DATA_COL_PX} whiteSpace="nowrap">Last Pts</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {leaderboardRows.length > 0 ? (
                      leaderboardRows.map((row, index) => {
                        const isCurrentUser = isLoggedInUserRow(row);
                        const rowBg = isCurrentUser ? "yellow.100" : "white";
                        const rowKey = (row?.user || row?.username || "").trim().toLowerCase();
                        // A pick whose fixture hasn't kicked off comes back
                        // nulled, so it reads as no pick rather than as a 0.
                        const lastPickRaw = lastPickByUsername[rowKey];
                        const lastPick =
                          lastPickRaw && lastPickRaw.revealed === false ? null : lastPickRaw;
                        const lastPickName = lastPick
                          ? (lastPick.picked_team_short_name || lastPick.picked_team_name || "-") + (lastPick.is_auto_pick ? " (A)" : "")
                          : "-";
                        const lastPts =
                          lastPick != null && lastPick.points_awarded != null
                            ? Number(lastPick.points_awarded)
                            : null;
                        const lastPtsText = lastPts !== null
                          ? lastPts > 0 ? `+${lastPts}` : `${lastPts}`
                          : "-";
                        const lastPtsColor = lastPts === null
                          ? "gray.400"
                          : lastPts > 0 ? "green.600"
                          : lastPts < 0 ? "red.600"
                          : "gray.700";

                        return (
                          <Tr
                            key={`${row?.user || row?.username || "row"}-${index}`}
                            bg={rowBg}
                          >
                            <Td
                              className="pl-sticky-col"
                              fontWeight={isCurrentUser ? "bold" : "normal"}
                              position="sticky"
                              left={0}
                              zIndex={2}
                              bg={rowBg}
                              w={LB_RANK_COL_WIDTH}
                              minW={LB_RANK_COL_WIDTH}
                              px={LB_FROZEN_COL_PX}
                            >
                              <WinnerRank rank={row?.rank} finished={seasonComplete} />
                            </Td>
                            <Td
                              className="pl-sticky-col"
                              fontWeight={isCurrentUser ? "bold" : "normal"}
                              position="sticky"
                              left={LB_RANK_COL_WIDTH}
                              zIndex={2}
                              bg={rowBg}
                              w={LB_USERNAME_COL_WIDTH}
                              minW={LB_USERNAME_COL_WIDTH}
                              px={LB_FROZEN_COL_PX}
                            >
                              {/* A td's width is only a hint under auto table
                                  layout, so the clamp lives on an inner box
                                  where maxW and text-overflow are honoured. */}
                              <Box
                                maxW={LB_USERNAME_TEXT_WIDTH}
                                overflow="hidden"
                                whiteSpace="nowrap"
                                textOverflow="ellipsis"
                                title={row?.user || row?.username || ""}
                              >
                                {row?.user || row?.username || "-"}
                              </Box>
                            </Td>
                            <Td isNumeric px={LB_DATA_COL_PX}
                                fontWeight={isCurrentUser ? "bold" : "normal"}>
                              {row?.points ?? 0}
                            </Td>
                            <Td isNumeric px={LB_DATA_COL_PX}
                                fontWeight={isCurrentUser ? "bold" : "normal"}>
                              {row?.goal_differential ?? 0}
                            </Td>
                            <Td isNumeric px={LB_DATA_COL_PX}
                                fontWeight={isCurrentUser ? "bold" : "normal"}>
                              {row?.win_streak ?? 0}
                            </Td>
                            <Td px={LB_DATA_COL_PX} whiteSpace="nowrap"
                                fontWeight={isCurrentUser ? "bold" : "normal"}>
                              {lastPickName}
                            </Td>
                            <Td isNumeric px={LB_DATA_COL_PX}
                                fontWeight="bold" color={lastPtsColor}>
                              {lastPtsText}
                            </Td>
                          </Tr>
                        );
                      })
                    ) : (
                      <Tr>
                        <Td colSpan={7}>
                          <Text color="gray.600">No leaderboard data available yet.</Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>

              <Text fontSize="xs" color="gray.500">A = autopick</Text>
            </VStack>
          </Box>

          {/* Match Fixtures / Results — 1/3 width */}
          <Box
            flex="1"
            minW={{ base: "100%", md: "280px" }}
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={6}
            minH={TOP_ROW_BOX_HEIGHT}
            display="flex"
            flexDirection="column"
          >
            <VStack align="stretch" spacing={4} flex="1">
              <Flex
                align="center"
                justify={{ base: "center", md: "space-between" }}
                direction={{ base: "column", md: "row" }}
                gap={2}
              >
                <Heading size="md" color={TOP_BAR_BLUE}
                         textAlign={{ base: "center", md: "left" }}>
                  Match Fixtures / Results
                </Heading>

                <HStack spacing={1} flexShrink={0}>
                  <Button
                    size="xs"
                    onClick={() => changeMatchweek((selectedMatchweek || 1) - 1)}
                    isDisabled={(selectedMatchweek || 1) <= 1 || loadingMatchweek}
                  >
                    ◀
                  </Button>

                  <Text fontWeight="bold" minW="42px" textAlign="center">
                    MW {selectedMatchweek || defaultMatchweek(currentMwMeta)}
                  </Text>

                  <Button
                    size="xs"
                    onClick={() => changeMatchweek((selectedMatchweek || 1) + 1)}
                    isDisabled={loadingMatchweek || (selectedMatchweek || 1) >= MAX_MATCHWEEK}
                  >
                    ▶
                  </Button>
                </HStack>
              </Flex>

              {loadingMatchweek ? (
                <Flex align="center" justify="center" py={10} flex="1">
                  <Spinner />
                </Flex>
              ) : (
                <VStack align="stretch" spacing={2} flex="1">
                  {fixtures.length > 0 ? (
                    fixtures.map((fixture, index) => {
                      const home = getTeamName(fixture?.home_team);
                      const away = getTeamName(fixture?.away_team);
                      const score =
                        fixture?.score &&
                        fixture.score.home != null &&
                        fixture.score.away != null
                          ? `${fixture.score.home} - ${fixture.score.away}`
                          : "vs";

                      return (
                        <Box
                          key={`${home}-${away}-${index}`}
                          border="1px solid #d9d9d9"
                          borderRadius="md"
                          px={4}
                          py={2}
                        >
                          <Flex justify="space-between" align="center" gap={4}>
                            <Text fontWeight="bold" fontSize="sm">
                              {home} {score} {away}
                            </Text>
                            {(() => {
                              const st = fixtureStatus(
                                fixture?.kickoff_utc, fixture?.is_final);
                              return (
                                <Text fontSize="xs" fontWeight="semibold"
                                      whiteSpace="nowrap" color={st.color}>
                                  {st.label}
                                </Text>
                              );
                            })()}
                          </Flex>
                          {fmtKickoff(fixture?.kickoff_utc) && (
                            <Text fontSize="0.65rem" color="gray.500" mt={0.5}>
                              {fmtKickoff(fixture.kickoff_utc)}
                            </Text>
                          )}
                        </Box>
                      );
                    })
                  ) : (
                    <Text color="gray.600">No fixtures found for this matchweek.</Text>
                  )}
                </VStack>
              )}
            </VStack>
          </Box>
        </HStack>

        <Box
          mt={8}
          bg={TOP_BAR_BLUE}
          borderTop={`2px solid ${DIVIDER_GRAY}`}
          borderBottom={`2px solid ${DIVIDER_GRAY}`}
          boxShadow="md"
          px={6}
          py={4}
          borderRadius="md"
        >
          <Heading
            fontFamily="'Orbitron', sans-serif"
            fontSize="28px"
            letterSpacing="2px"
            fontWeight="600"
          >
            TABLE PREDICTOR
          </Heading>
        </Box>

        <HStack align="start" spacing={6} flexWrap="wrap" mt={4} mb={4}>
          <Button
            bg="red.600"
            color="white"
            _hover={{ bg: "red.700" }}
            onClick={() => navigate(`/premier-league/table-predictor/${poolId}`, { state: { seasonId: SEASON_ID } })}
          >
            Make / Review Table Prediction
          </Button>
        </HStack>

        <HStack align="stretch" spacing={6} flexWrap="wrap" mt={2}>
          {/* Current Table */}
          <Box
            flex="1"
            minW={{ base: "100%", md: "280px" }}
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={6}
          >
            <VStack align="stretch" spacing={2}>
              <Flex align="center" justify="space-between" flexWrap="wrap" gap={3} mb={2} minH="40px">
                <Heading size="md" color={TOP_BAR_BLUE} lineHeight="1">
                  Current Table
                </Heading>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor={TOP_BAR_BLUE}
                  color={TOP_BAR_BLUE}
                  onClick={onExpandedTableOpen}
                  _hover={{ bg: "gray.100" }}
                >
                  Expanded Stats
                </Button>
              </Flex>

              <Box overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th w="48px" whiteSpace="nowrap">Rank</Th>
                      <Th whiteSpace="nowrap">Team</Th>
                      <Th isNumeric whiteSpace="nowrap">Pts</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {standingsRows.length > 0 ? (
                      <>
                        {standingsRows.map((row) => (
                          <Tr key={`${row.team_id}-${row.rank}`}>
                            <Td w="48px" whiteSpace="nowrap">
                              {row.rank ?? "-"}
                            </Td>
                            <Td whiteSpace="nowrap">
                              <HStack spacing={2} align="center">
                                {row.logo_url && (
                                  <Image src={row.logo_url} boxSize="18px" objectFit="contain" flexShrink={0} />
                                )}
                                <span>{row.team_name || "-"}</span>
                              </HStack>
                            </Td>
                            <Td isNumeric whiteSpace="nowrap">{row.points ?? 0}</Td>
                          </Tr>
                        ))}
                        <Tr>
                          <Td colSpan={3} bg="white" h="49px" borderBottom="none" />
                        </Tr>
                      </>
                    ) : (
                      <Tr>
                        <Td colSpan={3}>
                          <Text color="gray.600">No standings yet.</Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          </Box>

          {/* Table Predictions */}
          <Box
            flex="2"
            minW={{ base: "100%", md: "480px" }}
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={6}
            display="flex"
            flexDirection="column"
          >
            <VStack align="stretch" spacing={2} flex={1}>
              <Flex align="center" justify="space-between" flexWrap="wrap" gap={3} mb={2} minH="40px">
                <Heading size="md" color={TOP_BAR_BLUE} lineHeight="1">
                  Table Predictions  - Sorted by Score
                </Heading>
              </Flex>

              {/* Shown from the first submission rather than only after the
                  lock, matching the NFL division board: you can see who is in
                  and your own table, while everyone else's stays redacted by
                  the server until MW4. Hiding the whole box meant nobody could
                  tell who had entered — or review their own picks here. */}
              {!(tablePredictorData?.predictions?.length > 0) ? (
                <VStack align="center" justify="center" py={8} spacing={2}>
                  <Text color="gray.500" textAlign="center">
                    {tablePredictorData?.locked
                      ? "No predictions were submitted."
                      : "Predictions are open until MW4 kicks off."}
                  </Text>
                  {!tablePredictorData?.locked && (
                    <Text color="gray.400" fontSize="sm" textAlign="center">
                      Submit your predicted final table before it locks!
                    </Text>
                  )}
                </VStack>
              ) : tablePredictorData?.predictions?.length > 0 ? (
                <Box overflowX="auto" flex={1} minH={0}>
                  {(() => {
                    const preds = [...tablePredictorData.predictions].sort(
                      (a, b) => (a.score ?? Infinity) - (b.score ?? Infinity)
                    );
                    return (
                      <Table
                        size="sm"
                        variant="simple"
                        sx={{
                          height: "100%",
                          "& tbody": { height: "100%" },
                          "& tbody tr": { height: "1px" },
                        }}
                      >
                        <Thead>
                          <Tr>
                            <Th w="48px" whiteSpace="nowrap">Pos</Th>
                            {preds.map((p, ci) => {
                              const isMe = p.username?.trim().toLowerCase() === username.trim().toLowerCase();
                              return (
                                <Th
                                  key={p.user_id}
                                  whiteSpace="nowrap"
                                  minW="140px"
                                  sx={{ textAlign: "left !important" }}
                                  bg={isMe ? "yellow.100" : ci % 2 === 0 ? "white" : "gray.50"}
                                  fontWeight={isMe ? "bold" : "normal"}
                                >
                                  <HStack spacing={1} align="baseline" justify="flex-start">
                                    <WinnerName
                                      name={p.username}
                                      size={12}
                                      isWinner={seasonComplete
                                                && tableWinnerScore !== null
                                                && p.score === tableWinnerScore}
                                    />
                                    <Text fontSize="xs" fontWeight="bold"
                                          color={tablePredictorData?.locked
                                                 ? TOP_BAR_BLUE : "green.600"}>
                                      {/* A score before the lock is measured
                                          against three matchweeks of table and
                                          reads as 0 before any are played, so
                                          it would flatter everyone. Until then
                                          the useful fact is simply that they
                                          are in. */}
                                      {tablePredictorData?.locked
                                        ? (p.score ?? "DNF")
                                        : "Saved"}
                                    </Text>
                                  </HStack>
                                </Th>
                              );
                            })}
                          </Tr>
                        </Thead>
                        <Tbody>
                          {Array.from({ length: 20 }, (_, i) => (
                            <Tr key={i + 1}>
                              <Td w="48px" color={TOP_BAR_BLUE} whiteSpace="nowrap">{i + 1}</Td>
                              {preds.map((p, ci) => {
                                const isMe = p.username?.trim().toLowerCase() === username.trim().toLowerCase();
                                return (
                                  <Td
                                    key={p.user_id}
                                    whiteSpace="nowrap"
                                    textAlign="left"
                                    bg={isMe ? "yellow.100" : ci % 2 === 0 ? "white" : "gray.50"}
                                    fontWeight={isMe ? "bold" : "normal"}
                                  >
                                    {/* Another player's table arrives empty
                                        from the server until the lock, so this
                                        dashes out rather than leaking. */}
                                    {p.positions?.[i]?.team_name || "—"}
                                  </Td>
                                );
                              })}
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    );
                  })()}
                </Box>
              ) : (
                <Text color="gray.500" textAlign="center" py={4}>
                  No predictions submitted yet.
                </Text>
              )}
            </VStack>
          </Box>
        </HStack>
          </>
        )}
      </Container>

      <ExpandedTableModal
        isOpen={isExpandedTableOpen}
        onClose={onExpandedTableClose}
        standingsRows={standingsRows}
      />

      {!isArchived && (
        <RulesPayoutsPanel
          rules={PL_RULES}
          payouts={poolPayouts}
          totalPot={poolDetail?.total_pot}
        />
      )}
    </Box>
  );
}